import { useState, useCallback } from 'react';
import { CategoryYearBudgetDto, UpsertCategoryYearBudgetsCommand, ExpenseType } from '../types/homeExpenses';
import { getCategoryYearBudgets, upsertCategoryYearBudgets } from '../services/homeExpensesApi';
import { useExpensesYear } from '../context/ExpensesYearContext';

export const useBudgets = (initialType: ExpenseType = ExpenseType.Home) => {
  const { year } = useExpensesYear();
  const [type, setType] = useState<ExpenseType>(initialType);
  const [budgets, setBudgets] = useState<CategoryYearBudgetDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const fetchBudgets = useCallback(async (y?: number, t?: ExpenseType) => {
    setLoading(true);
    setError(null);
    try {
      const res = await getCategoryYearBudgets(y ?? year, t ?? type);
      setBudgets(res);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Não foi possível carregar os orçamentos');
    } finally {
      setLoading(false);
    }
  }, [year, type]);

  const updateBudget = useCallback((categoryPublicId: string, monthlyBudgetAmount: number) => {
    setBudgets(prev => prev.map(b => b.categoryPublicId === categoryPublicId ? { ...b, monthlyBudgetAmount } : b));
  }, []);

  const saveBudgets = useCallback(async () => {
    setSaving(true);
    setError(null);
    try {
      const command: UpsertCategoryYearBudgetsCommand = {
        year,
        items: budgets.map(b => ({ categoryPublicId: b.categoryPublicId, monthlyBudgetAmount: b.monthlyBudgetAmount || 0 })),
      };
      await upsertCategoryYearBudgets(command);
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Não foi possível guardar os orçamentos');
      throw err;
    } finally {
      setSaving(false);
    }
  }, [year, budgets]);

  return {
    year,
    type,
    setType,
    budgets,
    loading,
    error,
    saving,
    refresh: () => fetchBudgets(year, type),
    updateBudget,
    saveBudgets,
  };
};
